export interface RefreshHandler {
    /**
     * Initializes the refresh handler.
     * Will be called inside `app:mounted` lifecycle hook.
     */
    init: () => void;

    /**
     * Handles cleanup of the refresh handler. Will be called on `unmount` app hook.
     */
    destroy: () => void;
}

export interface DefaultRefreshHandlerConfig {
    /**
     * Whether to refresh the session every `X` milliseconds. Set this to `false` to turn it off. The session will only be refreshed if a session already exists.
     *
     * Setting this to `true` will refresh the session every second.
     * Setting this to `false` will turn off session refresh.
     * Setting this to a number `X` will refresh the session every `X` milliseconds.
     *
     * @example 1000
     * @default false
     */
    enablePeriodically?: number | boolean;
    /**
     * Whether to refresh the session every time the browser window is refocused.
     *
     * @example false
     * @default true
     */
    enableOnWindowFocus?: boolean;
}

export class DefaultRefreshHandler implements RefreshHandler {
    /** Result of `useUserSession` composable, mostly used for session data/refreshing */
    session?: ReturnType<typeof useUserSession>;

    /** Refetch interval */
    refetchIntervalTimer?: ReturnType<typeof setInterval>;

    // TODO: find more Generic method to start a Timer for the Refresh Token
    /** Refetch interval for local/refresh schema */
    refreshTokenIntervalTimer?: ReturnType<typeof setInterval>;

    /** Because passing `this.visibilityHandler` to `document.addEventHandler` loses `this` context */
    private boundVisibilityHandler: typeof this.visibilityHandler;

    constructor(public config: DefaultRefreshHandlerConfig) {
        this.boundVisibilityHandler = this.visibilityHandler.bind(this);
    }

    private get isLoggedIn(): boolean {
        return this.session?.loggedIn.value ?? false;
    }

    private async refresh(): Promise<void> {
        if (!this.session) {
            console.warn('Session is not initialized, cannot refresh.');
            return;
        }

        if (location.pathname?.startsWith('/auth/signout')) {
            // If the user is signing out, we don't want to refresh the session
            return;
        }

        // await this.session?.fetch();

        const [error, _] = await catchError(fetchUser());

        // If there is no error, we can assume the session is refreshed
        if (!error) {
            return;
        }

        // Refresh the access token
        const [refreshError, refreshResponse] =
            await catchError(refreshAccessToken());

        if (refreshError) {
            // If there is an error refreshing the access token, we log it
            logout('/');
            return;
        }

        // Refresh the user session
        await fetchUser(refreshResponse.accessToken);

        // refresh the session
        await this.session.fetch();
    }

    init(): void {
        this.session = useUserSession();

        document.addEventListener(
            'visibilitychange',
            this.boundVisibilityHandler,
            false,
        );

        const { enablePeriodically } = this.config;

        if (!enablePeriodically) {
            return;
        }

        const intervalTime =
            enablePeriodically === true ? 1000 : enablePeriodically;

        this.refetchIntervalTimer = setInterval(() => {
            if (this.isLoggedIn) {
                // If the user is logged in, refresh the session
                this.refresh();
            }
        }, intervalTime);
    }

    destroy(): void {
        // Clear visibility handler
        document.removeEventListener(
            'visibilitychange',
            this.boundVisibilityHandler,
            false,
        );

        // Clear refetch interval
        clearInterval(this.refetchIntervalTimer);

        // Clear refetch interval
        if (this.refreshTokenIntervalTimer) {
            clearInterval(this.refreshTokenIntervalTimer);
        }

        // Release state
        this.session = undefined;
    }

    visibilityHandler(): void {
        // Listen for when the page is visible, if the user switches tabs
        // and makes our tab visible again, re-fetch the session, but only if
        // this feature is not disabled.
        if (
            this.config?.enableOnWindowFocus &&
            document.visibilityState === 'visible' &&
            this.isLoggedIn
        ) {
            this.refresh();
        }
    }
}

/**
 * AuthCheck Plugin for Nuxt 3
 * Uses the `useUserSession` composable to manage user sessions.
 * This plugin checks if the user is authenticated.
 */
export default defineNuxtPlugin((nuxtApp) => {
    const _refreshHandler = new DefaultRefreshHandler({
        enablePeriodically: 5 * 1000 * 60, // Refresh every 5 minutes
        enableOnWindowFocus: true, // Refresh on window focus
    });

    // 2. Setup session maintanence, e.g., auto refreshing or refreshing on foux
    nuxtApp.hook('app:mounted', () => {
        // Initialize the refresh handler
        _refreshHandler.init();
    });

    const _unmount = nuxtApp.vueApp.unmount;
    nuxtApp.vueApp.unmount = function () {
        _refreshHandler.destroy();

        // Call original unmount
        _unmount();
    };
});
